import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  updateDoc,
  doc,
  serverTimestamp,
  onSnapshot,
  getDoc,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';
import { createMessageNotification } from './notificationService';

/**
 * Send a message
 * @param {Object} messageData - Message data
 * @returns {Promise<string>} - Message ID
 */
export const sendMessage = async (messageData) => {
  try {
    const message = {
      ...messageData,
      timestamp: serverTimestamp(),
      isRead: false,
      isEdited: false,
      editedAt: null
    };

    const messageRef = await addDoc(collection(db, 'messages'), message);

    // Create notification for recipient only when recipient is different from sender
    // (e.g. in demo/seed project the same user can be both student and "company", so skip to avoid self-notifications)
    if (messageData.recipientId !== messageData.senderId) {
      try {
        let senderName = 'User';
        let projectTitle = 'Project';

        if (messageData.senderType === 'student') {
          const senderDoc = await getDoc(doc(db, 'studentProfiles', messageData.senderId));
          if (senderDoc.exists()) {
            const senderData = senderDoc.data();
            senderName = senderData.fullName || `${senderData.firstName || ''} ${senderData.lastName || ''}`.trim() || 'Student';
          }
        } else if (messageData.senderType === 'company') {
          const senderDoc = await getDoc(doc(db, 'companyProfiles', messageData.senderId));
          if (senderDoc.exists()) {
            const senderData = senderDoc.data();
            senderName = senderData.companyName || 'Company';
          }
        }

        if (messageData.projectId) {
          const projectDoc = await getDoc(doc(db, 'projects', messageData.projectId));
          if (projectDoc.exists()) {
            projectTitle = projectDoc.data().title || 'Project';
          }
        }

        await createMessageNotification(
          messageData.recipientId,
          messageData.senderId,
          senderName,
          messageData.senderType,
          messageData.projectId,
          projectTitle,
          messageData.content
        );
      } catch (notificationError) {
        console.error('Error creating message notification:', notificationError);
      }
    }

    // Create activity for tracking
    await addDoc(collection(db, 'activities'), {
      projectId: messageData.projectId,
      type: 'message',
      userId: messageData.senderId,
      userType: messageData.senderType,
      content: `sent a message`,
      timestamp: serverTimestamp()
    });

    return messageRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw new Error('Failed to send message: ' + error.message);
  }
};

/**
 * Get conversation between two users in a project
 */
export const getConversation = async (projectId, userId1, userId2) => {
  try {
    // Simpler query that doesn't require complex index
    const q = query(
      collection(db, 'messages'),
      where('projectId', '==', projectId),
      where('senderId', 'in', [userId1, userId2]),
      orderBy('timestamp', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(msg => msg.senderId === userId1 || msg.senderId === userId2);
  } catch (error) {
    throw new Error('Failed to get conversation: ' + error.message);
  }
};

/**
 * Subscribe to conversation updates
 */
export const subscribeToConversation = (projectId, userId1, userId2, callback) => {
  // Query for messages where either user is sender or recipient
  const q = query(
    collection(db, 'messages'),
    where('projectId', '==', projectId),
    where('participants', 'array-contains', userId1),
    orderBy('timestamp', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(msg => 
        (msg.senderId === userId1 && msg.recipientId === userId2) ||
        (msg.senderId === userId2 && msg.recipientId === userId1)
      );
    callback(messages);
  });
};

/**
 * Mark message as read
 */
export const markMessageAsRead = async (messageId) => {
  try {
    await updateDoc(doc(db, 'messages', messageId), {
      isRead: true
    });
    return true;
  } catch (error) {
    throw new Error('Failed to mark message as read: ' + error.message);
  }
};

/**
 * Mark all messages in a conversation as read
 */
export const markConversationAsRead = async (projectId, userId1, userId2) => {
  try {
    // Simpler query that doesn't require complex index
    const q = query(
      collection(db, 'messages'),
      where('projectId', '==', projectId),
      where('recipientId', '==', userId1),
      where('isRead', '==', false)
    );

    const snapshot = await getDocs(q);
    const unreadMessages = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(msg => msg.senderId === userId2);

    await Promise.all(
      unreadMessages.map(msg =>
        updateDoc(doc(db, 'messages', msg.id), { isRead: true })
      )
    );

    return true;
  } catch (error) {
    throw new Error('Failed to mark conversation as read: ' + error.message);
  }
};

/**
 * Get unread message count for a user
 */
export const getUnreadMessageCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'messages'),
      where('recipientId', '==', userId),
      where('isRead', '==', false)
    );

    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    throw new Error('Failed to get unread message count: ' + error.message);
  }
}; 